export interface SilenceDetectorOptions {
  /** How long continuous silence must last before onSilence fires. */
  silenceDurationMs?: number;
  /** Peak sample deviation (0-1) below which a check interval counts as silence. */
  volumeThreshold?: number;
  /** How often to sample the audio level. */
  checkIntervalMs?: number;
  /**
   * Diagnostic hook: called every check interval with the raw peak level
   * measured and whether it counted as silence — for calibrating
   * `volumeThreshold` against real hardware. Never used for detection logic
   * itself.
   */
  onLevel?: (peak: number, isSilent: boolean) => void;
}

const DEFAULT_SILENCE_DURATION_MS = 500;
// Peak-based, not averaged: a single loud sample within the window is
// enough to count it as "not silence". This intentionally errs toward
// under-triggering silence rather than over-triggering it — the failure
// mode we actually care about avoiding is cutting someone off mid-sentence,
// not tolerating one extra 200ms tick of hesitation. Calibrated against
// real hardware: observed peaks were ~0.03 for room-noise silence and
// ~0.4 for normal speech — 0.05 sits with a wide margin above the noise
// floor and well below speech on both sides.
const DEFAULT_VOLUME_THRESHOLD = 0.05;
const DEFAULT_CHECK_INTERVAL_MS = 200;
// A time-domain sample is only ever `fftSize` samples long — at 44.1-48kHz
// a small fftSize (e.g. the AnalyserNode default of 2048, or 512) covers
// just a few milliseconds, leaving most of each checkIntervalMs gap
// unobserved. 16384 comfortably covers a full ~200ms window across common
// sample rates so a quiet word landing between ticks can't be missed.
const ANALYSER_FFT_SIZE = 16384;

function getAudioContextCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/**
 * Watches a live MediaStream's volume and fires a callback once it's been
 * continuously below threshold for `silenceDurationMs` — including from the
 * very start if the user never makes a sound at all. Used to auto-stop
 * listening without requiring the user to tap the mic again.
 *
 * Silently does nothing in environments without Web Audio API support
 * (e.g. jsdom in tests) — auto-stop is a convenience, never a hard
 * requirement; manual stop always still works regardless.
 */
export class SilenceDetector {
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  start(stream: MediaStream, onSilence: () => void, options: SilenceDetectorOptions = {}): void {
    this.stop();

    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) return;

    const silenceDurationMs = options.silenceDurationMs ?? DEFAULT_SILENCE_DURATION_MS;
    const volumeThreshold = options.volumeThreshold ?? DEFAULT_VOLUME_THRESHOLD;
    const checkIntervalMs = options.checkIntervalMs ?? DEFAULT_CHECK_INTERVAL_MS;
    const onLevel = options.onLevel;

    try {
      this.audioContext = new AudioContextCtor();
      this.source = this.audioContext.createMediaStreamSource(stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = ANALYSER_FFT_SIZE;
      this.source.connect(this.analyser);
    } catch {
      // Some hosts (e.g. autoplay-policy-restricted contexts) can reject
      // AudioContext construction — fall back to no auto-stop.
      this.stop();
      return;
    }

    // getByteTimeDomainData wants a buffer sized to fftSize, not
    // frequencyBinCount (fftSize/2 — that's for frequency-domain data) —
    // using the wrong size here silently samples only half the waveform.
    const data = new Uint8Array(this.analyser.fftSize);
    // Counted in ticks (not wall-clock time) so this holds up under faked
    // timers in tests and is immune to any clock/performance.now() quirks.
    let silentTicks = 0;
    const ticksUntilSilent = Math.max(1, Math.round(silenceDurationMs / checkIntervalMs));

    this.intervalId = setInterval(() => {
      if (!this.analyser) return;
      this.analyser.getByteTimeDomainData(data);

      let peak = 0;
      for (const byte of data) {
        const deviation = Math.abs(byte - 128) / 128;
        if (deviation > peak) peak = deviation;
      }

      const isSilent = peak <= volumeThreshold;
      onLevel?.(peak, isSilent);

      if (!isSilent) {
        silentTicks = 0;
        return;
      }
      silentTicks += 1;
      if (silentTicks >= ticksUntilSilent) {
        this.stop();
        onSilence();
      }
    }, checkIntervalMs);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.source?.disconnect();
    this.analyser?.disconnect();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      void this.audioContext.close().catch(() => {});
    }
    this.source = null;
    this.analyser = null;
    this.audioContext = null;
  }
}
